import { Box } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import type { JSONContent } from '@tiptap/core';
import { Editor, EditorContent } from '@tiptap/react';
import { BerryLoader } from '@web-app/components/BerryLoader';
import { EDITOR_LOG_PREFIX } from '@web-app/utils/api/logger-prefixes';
import { FC, useEffect, useRef } from 'react';

import {
  EDITOR_LOADING_HEIGHT,
  EDITOR_MIN_HEIGHT,
  EditorName,
} from './constants';
import { TipTapToolbar } from './TipTapToolbar';
import { useTipTapEditor } from './useTipTapEditor';

interface TipTapEditorProps {
  content?: string | object; // Can be HTML string or TipTap JSON object
  onChange?: (contentHtml: string, contentJson: JSONContent) => void;
  onEditorReady?: (editor: Editor) => void; // Callback when editor is ready with editor instance
  placeholder?: string;
  showToolbar?: boolean; // Control whether to show the toolbar (default: true)
  showBorder?: boolean; // Control whether to show the editor border (default: true)
  editable?: boolean; // Control whether the editor is editable (default: true)
  ariaLabel?: string; // Accessible name applied to the contenteditable element
  name: EditorName; // Identifier for logging and analytics to track editor usage context (e.g. "in-session-provider-notes","post-session-follow-up-message", etc)
  /** See {@link UseTipTapEditorOptions.onImageFiles}. */
  onImageFiles?: (files: File[]) => void;
}

/**
 * Decides whether the content-sync effect should bail out during the editor's
 * initialization window.
 *
 * onCreate loads the initial content and then clears `isInitializing` on the
 * next tick, so normally we skip this effect's first run to avoid re-applying
 * identical content (which would pollute the undo history).
 *
 * The exception is the async-data race: the editor mounted with empty content
 * (data not ready yet) and the real content arrives while still initializing.
 * In that case the editor is still empty, so we must apply the content instead
 * of dropping it — otherwise the editor stays blank (e.g. the follow-up drawer).
 */
export const shouldSkipInitialContentUpdate = (
  isInitializing: boolean,
  editorIsEmpty: boolean,
  hasContent: boolean,
): boolean => {
  if (!isInitializing) return false;
  const contentArrivedAfterEmptyMount = editorIsEmpty && hasContent;
  return !contentArrivedAfterEmptyMount;
};

/**
 * TipTap WYSIWYG editor component
 * Provides a rich text editing experience with formatting options
 */
export const TipTapEditor: FC<TipTapEditorProps> = ({
  content = '',
  onChange,
  onEditorReady,
  placeholder = 'Start typing...',
  showToolbar = true,
  showBorder = true,
  editable = true,
  ariaLabel,
  name,
  onImageFiles,
}) => {
  const isInitializing = useRef(true);
  const initialContent = typeof content === 'object' ? content : content || '';

  const { editor, isReady } = useTipTapEditor({
    initialContent,
    editable,
    placeholder,
    name,
    ariaLabel,
    onChange,
    isInitializing,
    onEditorReady,
    onImageFiles,
  });

  // Update editor's editable state when prop changes
  useEffect(() => {
    if (!editor || !isReady) return;
    editor.setEditable(editable);
  }, [editor, isReady, editable]);

  // Update editor content when prop changes (only after editor is ready)
  useEffect(() => {
    if (!editor || !isReady) return;

    // Skip the initial run: onCreate already loaded the content. Re-applying it
    // here would be redundant and, because `getHTML()` reflects normalized
    // markup (e.g. the lineHeight default) that the raw `content` prop lacks,
    // the comparison below always sees a "change" and calls setContent again —
    // which pollutes the undo history (undo would appear available on load).
    //
    // Exception: when onCreate ran with empty content (async data hadn't
    // arrived yet) and the real content shows up during this same
    // initialization window, the editor is still empty. We must apply it here,
    // otherwise the content is silently dropped and the editor stays blank.
    if (
      shouldSkipInitialContentUpdate(
        isInitializing.current,
        editor.isEmpty,
        !!content,
      )
    ) {
      return;
    }

    // Handle empty content
    if (!content || (typeof content === 'string' && content.trim() === '')) {
      const currentContent = editor.getHTML();
      if (currentContent.trim() !== '') {
        editor.commands.clearContent();
      }
      return;
    }

    // Get current content for comparison - normalize both to JSON strings for accurate comparison
    let currentContent: string;
    let newContentString: string;

    try {
      if (typeof content === 'object') {
        currentContent = JSON.stringify(editor.getJSON());
        newContentString = JSON.stringify(content);
      } else {
        currentContent = editor.getHTML();
        newContentString = content;
      }

      // Only update if content has actually changed
      if (currentContent !== newContentString) {
        // Preserve focus state before updating content
        const wasFocused = editor.isFocused;
        const selection = editor.state.selection;

        editor.commands.setContent(content, { emitUpdate: false });

        // Restore focus if editor was focused before
        if (wasFocused) {
          // Use setTimeout to ensure setContent completes first
          setTimeout(() => {
            editor.commands.focus();
            // Try to restore selection if possible
            if (selection && editor.state.doc.content.size > 0) {
              try {
                editor.commands.setTextSelection(selection);
              } catch {
                // Selection might be invalid after content change, just focus
                editor.commands.focus();
              }
            }
          }, 0);
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      console.error('Error updating TipTap content:', error);
      console.error('Invalid content value:', content);

      datadogLogs.logger.error(
        `${EDITOR_LOG_PREFIX} content update failed`,
        {
          error: errorMessage,
          contentType: typeof content,
          editorName: name,
        },
        error instanceof Error ? error : undefined,
      );
    }
  }, [content, editor, isReady, name]);

  if (!editor || !isReady) {
    return <BerryLoader height={EDITOR_LOADING_HEIGHT} />;
  }

  return (
    <Box
      data-testid="tiptap-editor"
      {...(showBorder
        ? {
            border: '1px solid',
            borderColor: 'gray.200',
            borderRadius: 'md',
          }
        : {})}
      minH={EDITOR_MIN_HEIGHT}
      isolation="isolate"
      sx={{
        '.tiptap-editor': {
          outline: 'none',
          minHeight: EDITOR_MIN_HEIGHT,
          '& p.is-editor-empty:first-of-type::before': {
            content: 'attr(data-placeholder)',
            float: 'left',
            color: '#9ca3af',
            pointerEvents: 'none',
            height: 0,
          },
          '& p': {
            margin: '0.15rem 0',
          },
          '& h1, & h2, & h3': {
            margin: '1rem 0 0.5rem 0',
            fontWeight: 'bold',
          },
          '& h1': {
            fontSize: '1.5rem',
          },
          '& h2': {
            fontSize: '1.25rem',
          },
          '& h3': {
            fontSize: '1.1rem',
          },
          '& ul': {
            paddingLeft: '1.5rem',
            margin: '0.5rem 0',
            listStyleType: 'disc',
          },
          '& ol': {
            paddingLeft: '1.5rem',
            margin: '0.5rem 0',
            listStyleType: 'decimal',
          },
          '& blockquote': {
            borderLeft: '3px solid #e5e7eb',
            paddingLeft: '1rem',
            margin: '0.5rem 0',
            fontStyle: 'italic',
            color: '#6b7280',
          },
          '& code': {
            backgroundColor: '#f3f4f6',
            padding: '0.2rem 0.4rem',
            borderRadius: '0.25rem',
            fontSize: '0.875rem',
            fontFamily: 'monospace',
          },
          '& pre': {
            backgroundColor: '#1f2937',
            color: '#f9fafb',
            padding: '1rem',
            borderRadius: '0.5rem',
            overflow: 'auto',
            margin: '0.5rem 0',
            '& code': {
              backgroundColor: 'transparent',
              padding: 0,
              color: 'inherit',
            },
          },
          '& [style*="text-align: center"]': {
            textAlign: 'center',
          },
          '& [style*="text-align: right"]': {
            textAlign: 'right',
          },
          '& [style*="text-align: left"]': {
            textAlign: 'left',
          },
          '& a': {
            color: '#3182ce',
            textDecoration: 'underline',
            cursor: 'pointer',
            '&:hover': {
              color: '#2c5282',
            },
          },
          '& img.tiptap-image': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '0.375rem',
            margin: '0.5rem 0',
            display: 'block',
          },
          '& [data-type="callout-node"]': {
            backgroundColor: 'bg.subtle',
            borderRadius: '6px',
            borderLeft: '3px solid #CBD5E0',
            padding: '16px',
            margin: '8px 0',
          },
        },
      }}
    >
      {/* Toolbar */}
      {showToolbar && <TipTapToolbar editor={editor} editable={editable} />}

      {/* Editor Content */}
      <Box p={showBorder ? 4 : 0}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};
